import * as Sentry from "@sentry/vue";

/**
 * Which Hasura write rails this environment has already told us it cannot use.
 *
 * Two of the four rails are deployed ahead of their permissions — the project row and the
 * special-conditions flag, whose tables carry select permissions only — so every call to them raises
 * `ProjectWriteUnavailableError` and the caller falls back to the REST base-info PUT. That answer is
 * a property of the ENVIRONMENT's schema, not of the request: Hasura generates no `update_project_by_pk`
 * field for the role at all, so the second ask cannot come back differently from the first.
 *
 * Without this, learning it cost two GraphQL round-trips and a Sentry event on EVERY save — and
 * learning it twice costs exactly as much as learning it once while telling you nothing new.
 *
 * Module state, so it lives as long as the tab. A reload re-asks, which is what makes the day the
 * permission lands cost a refresh rather than a deploy. Deliberately not per project: the fact is
 * about the role's schema, and every project in the session sees the same one.
 */
export type ProjectWriteRail =
  | "projectRow"
  | "specialConditionConfig"
  | "projectModels"
  | "projectCalculation"
  | "projectReferenceAreas"
  | "projectBuildingAreas";

const unavailableRails = new Set<ProjectWriteRail>();

export const isRailUnavailable = (rail: ProjectWriteRail): boolean => unavailableRails.has(rail);

/**
 * Record that a rail is not writable here, reporting it the FIRST time only.
 *
 * The report is worth one event per session — it is how we find out an environment is still on the
 * fallback — and worthless once per save, which is what it was: the same expected, environment-wide
 * condition, filed again on every click of Save.
 */
export const noteRailUnavailable = (rail: ProjectWriteRail, error: unknown): void => {
  if (unavailableRails.has(rail)) return;

  unavailableRails.add(rail);
  Sentry.captureException(error);
};

/** Clears the memo. For tests, which would otherwise carry one case's verdict into the next. */
export const resetProjectWriteAvailability = (): void => unavailableRails.clear();
